import type { IconBoxColor } from '@/components/ui';
import { Flex, Heading, IconBox, Section, Text } from '@/components/ui';

import { cloneElement, CSSProperties, isValidElement, ReactNode } from 'react';

import { IconFileSearch } from '@tabler/icons-react';
import clsx from 'clsx';
import { motion } from 'framer-motion';

type EmptyContentState = 'default' | 'error' | 'warning' | 'info';
type EmptyContentVariant = 'default' | 'outlined' | 'surface' | 'ghost';

type EmptyContentProps = {
  title?: string;
  description?: string | ReactNode;
  icon?: ReactNode;
  children?: ReactNode;
  className?: string;
  state?: EmptyContentState;
  variant?: EmptyContentVariant;
  size?: 'small' | 'medium' | 'large';
  disableAnimation?: boolean;
  withIconBox?: boolean;
  iconBoxClassName?: string;
  iconAnimationMode?: 'box' | 'icon';
  style?: CSSProperties;
};

const iconSizeClass = {
  large: 'empty-content__icon--large',
  medium: 'empty-content__icon--medium',
  small: 'empty-content__icon--small',
};

const iconBoxSizeClass = {
  large: 'empty-content__icon-box--large',
  medium: 'empty-content__icon-box--medium',
  small: 'empty-content__icon-box--small',
};

const stateAnimations = {
  default: {
    initial: {
      opacity: 0,
      y: 20,
      scale: 0.95,
    },
    animate: {
      opacity: 1,
      y: 0,
      scale: 1,
    },
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1],
      delay: 0.1,
    },
  },
  error: {
    initial: {
      opacity: 0,
      scale: 0.8,
      rotate: -10,
    },
    animate: {
      opacity: 1,
      scale: 1,
      rotate: 0,
    },
    transition: {
      duration: 0.4,
      ease: [0.34, 1.56, 0.64, 1],
      delay: 0.1,
    },
  },
  warning: {
    initial: {
      opacity: 0,
      scale: 0.8,
      y: 20,
    },
    animate: {
      opacity: 1,
      scale: 1,
      y: 0,
    },
    transition: {
      duration: 0.5,
      ease: [0.34, 1.56, 0.64, 1],
      delay: 0.1,
    },
  },
  info: {
    initial: {
      opacity: 0,
      scale: 0.8,
      rotate: 10,
    },
    animate: {
      opacity: 1,
      scale: 1,
      rotate: 0,
    },
    transition: {
      duration: 0.4,
      ease: [0.34, 1.56, 0.64, 1],
      delay: 0.1,
    },
  },
} as const;

const EmptyContent = ({
  title,
  size = 'large',
  state = 'default',
  variant = 'default',
  description,
  icon,
  children,
  className,
  disableAnimation = false,
  withIconBox = false,
  iconBoxClassName,
  iconAnimationMode = 'icon',
  style,
  ...props
}: EmptyContentProps) => {
  const getHeadingSize = () => {
    const sizes = {
      small: 'small',
      medium: 'medium',
      large: 'large',
    } as const;
    return sizes[size] || sizes.medium;
  };

  const getTextSize = () => {
    const sizes = {
      small: 'xsmall',
      medium: 'small',
      large: 'small',
    } as const;
    return sizes[size] || sizes.medium;
  };

  const getIconBoxColor = (): IconBoxColor => {
    const colors: Record<EmptyContentState, IconBoxColor> = {
      default: 'default',
      error: 'error',
      warning: 'warning',
      info: 'info',
    };
    return colors[state];
  };

  const renderIcon = () => {
    if (!icon) return null;

    const animation = stateAnimations[state];
    const iconElement = isValidElement(icon) ? cloneElement(icon) : icon;
    const iconClass = clsx('empty-content__icon', iconSizeClass[size]);
    const iconBoxClass = clsx(
      'empty-content__icon-box',
      iconBoxSizeClass[size],
      iconBoxClassName
    );
    const stateClass = `empty-content--${state}`;

    const iconContent = (
      <div className={iconClass}>{iconElement || <IconFileSearch />}</div>
    );

    if (disableAnimation) {
      return withIconBox ? (
        <IconBox
          className={clsx(iconBoxClass, stateClass)}
          size={size}
          color={getIconBoxColor()}
        >
          {iconContent}
        </IconBox>
      ) : (
        <div className={clsx(iconClass, stateClass)}>{iconContent}</div>
      );
    }

    const Wrapper = motion.div;

    if (withIconBox && iconAnimationMode === 'icon') {
      return (
        <IconBox
          className={clsx(iconBoxClass, stateClass)}
          size={size}
          color={getIconBoxColor()}
        >
          <Wrapper
            initial={animation.initial}
            animate={animation.animate}
            transition={animation.transition}
            className={iconClass}
          >
            {iconElement || <IconFileSearch />}
          </Wrapper>
        </IconBox>
      );
    }

    if (withIconBox) {
      return (
        <Wrapper
          initial={animation.initial}
          animate={animation.animate}
          transition={animation.transition}
          className={clsx(iconBoxClass, stateClass)}
        >
          <IconBox size={size} color={getIconBoxColor()}>
            {iconContent}
          </IconBox>
        </Wrapper>
      );
    }

    return (
      <Wrapper
        initial={animation.initial}
        animate={animation.animate}
        transition={animation.transition}
        className={clsx(iconClass, stateClass)}
      >
        {iconContent}
      </Wrapper>
    );
  };

  return (
    <Section
      component="flex"
      size={size}
      className={clsx(
        'empty-content',
        `empty-content--variant-${variant}`,
        className
      )}
      style={style}
      {...props}
    >
      <Flex
        direction="column"
        align="center"
        justify="center"
        gap="4"
        className="empty-content__inner"
      >
        {renderIcon()}
        <Flex direction="column" gap="2">
          {title && (
            <Heading
              highContrast
              size={getHeadingSize()}
              className={`title title--${size}`}
            >
              {title}
            </Heading>
          )}
          {description && (
            <Text
              size={getTextSize()}
              className={`max-w-3xl description description--${size}`}
            >
              {description}
            </Text>
          )}
        </Flex>
        {children}
      </Flex>
    </Section>
  );
};

export { EmptyContent };
